import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { AlertTriangle, CheckCircle2, Search as SearchIcon, ShieldAlert, X } from "lucide-react";
import Paywall from "../components/Paywall.jsx";
import { useIsPremium } from "../lib/premium";
import analytics from "../lib/analytics";

const sevClass = (s) => {
  const k = (s || "").toLowerCase();
  if (k.includes("contraindic")) return "bg-rose-200 text-rose-900 border-rose-300";
  if (k.includes("severe") || k.includes("major")) return "bg-rose-100 text-rose-800 border-rose-200";
  if (k.includes("moderate")) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-emerald-100 text-emerald-800 border-emerald-200";
};

export default function Interactions() {
  const premium = useIsPremium();
  const [drugs, setDrugs] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]); // ids
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [interactionCount, setInteractionCount] = useState(null);

  useEffect(() => { if (premium) api.get("/drugs").then((r) => setDrugs(r.data)).catch(() => {}); }, [premium]);
  useEffect(() => { api.get("/stats/counts").then((r) => setInteractionCount(r.data.interactions)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return drugs.filter((d) => !s || d.name.toLowerCase().includes(s) || (d.drug_class || "").toLowerCase().includes(s)).slice(0, 150);
  }, [drugs, q]);

  const selectedDrugs = drugs.filter((d) => selected.includes(d.drug_id));

  if (!premium) return <Paywall feature="The Drug Interaction Checker" />;

  const toggle = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
    setResult(null);
  };

  const check = async () => {
    if (selected.length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.post("/interactions/check", { drug_ids: selected });
      setResult(data);
      // GA4 drug_interaction_check — never send medication names (patient-safety).
      analytics.drugInteractionCheck(selected.length, (data?.pairs?.length || 0) > 0);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="interactions-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Safety</div>
        <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-[#111827]" /> Drug Interaction Checker
        </h1>
        <p className="text-slate-600 text-sm mt-1">Select 2+ drugs from {drugs.length} in the formulary to screen across {" "}
          <span className="font-medium">{(interactionCount ?? 786).toLocaleString("en-ZA")} known interactions</span>.
        </p>
      </div>

      {/* Selected chips — fixed min-height container so the list below never reflows on select/deselect */}
      <div className="flex flex-wrap items-center gap-2 min-h-[40px]" data-testid="selected-chips-row">
        {selectedDrugs.length === 0 ? (
          <span className="text-sm text-slate-400">Selected drugs will appear here — pick 2 or more below.</span>
        ) : (
          <>
            {selectedDrugs.map((d) => (
              <span key={d.drug_id} className="inline-flex items-center gap-1 bg-[#EFF4FF] text-[#111827] px-2 py-1 rounded-md text-sm">
                {d.name}
                <button onClick={() => toggle(d.drug_id)}><X className="w-3 h-3" /></button>
              </span>
            ))}
            <button onClick={() => { setSelected([]); setResult(null); }} className="text-xs text-slate-500 hover:text-slate-900">Clear all</button>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-[#111827]">Select drugs</h3>
              <Badge className="bg-[#EFF4FF] text-[#111827] border-0">{selected.length} selected</Badge>
            </div>
            <div className="relative mb-3">
              <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or class..." className="pl-9 bg-white" data-testid="interactions-search" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[500px] overflow-y-auto pr-1">
              {filtered.map((d) => (
                <label
                  key={d.drug_id}
                  className="flex items-center gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer"
                  data-testid={`drug-select-${d.drug_id}`}
                >
                  <Checkbox checked={selected.includes(d.drug_id)} onCheckedChange={() => toggle(d.drug_id)} />
                  <div className="text-sm min-w-0">
                    <div className="font-medium text-[#111827] truncate">{d.name}</div>
                    <div className="text-xs text-slate-500 truncate">{d.drug_class}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={check} disabled={selected.length < 2 || loading} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid="check-interactions-btn">
                {loading ? "Checking..." : "Check interactions"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 lg:col-span-1 self-start">
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold text-[#111827] mb-3">Result</h3>
            {!result ? (
              <div className="text-sm text-slate-500">Pick drugs and click "Check interactions".</div>
            ) : result.pairs.length === 0 ? (
              <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>No known interactions detected in our database. Always verify clinically.</div>
              </div>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                {result.pairs.map((p, i) => (
                  <div key={i} className={`p-3 rounded-md border text-sm ${sevClass(p.severity)}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold uppercase text-[10px] tracking-wide inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {p.severity}
                      </span>
                    </div>
                    <div className="font-medium mb-1">{p.drug_a} + {p.drug_b}</div>
                    <div className="text-xs mb-1.5">{p.effect}</div>
                    <div className="text-xs font-medium">→ {p.recommendation}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
